import { explorerUrl } from '@helpers/vivinoUrls';
import { Link } from '@vivino/js-react-common-ui';
import { useI18N } from '@vivino/js-web-common';
import PropTypes from 'prop-types';
import React from 'react';
import {
  ORDER_ASC,
  ORDER_BY_PRICE,
  ORDER_BY_RATINGS_COUNT,
  ORDER_DESC,
} from 'vivino-js/api/explore';
import { vintage } from 'vivino-js/apiPropTypes';
import { priceToString } from 'vivino-js/helpers/price';
import MerchantBadge from 'vivino-ui/icons/MerchantBadge/MerchantBadge';
import User from 'vivino-ui/icons/User/User';

import SsrSvg from '@components/SsrSvg/SsrSvg';

import BuyingOptions from '../../shared/components/BuyingOptions';

import styles from './purchaseAvailabilityPPC.scss';

const TRANSLATIONS_PATH = 'components.deal_wine_page.purchase_availability';
const TRANSLATIONS = {
  averageOfAllUsersReportedPrices: `${TRANSLATIONS_PATH}.average_of_all_users_reported_prices`,
  betterValue: `${TRANSLATIONS_PATH}.better_value`,
  betterValueTag: `${TRANSLATIONS_PATH}.better_value_tag`,
  goodToKnow: `${TRANSLATIONS_PATH}.good_to_know`,
  goodToKnowTag: `${TRANSLATIONS_PATH}.good_to_know_tag`,
  exploreSimilarWinesVariantD: `${TRANSLATIONS_PATH}.explore_similar_wines_variant_d`,
};

/**
 * Display the user or market median price details. If the data does not satisfy all conditions,
 * display nothing.
 */
const createMedianPriceWidget = ({ vintage, availability, marketInformation, t }) => {
  const availabilityMedianPrice = availability?.median?.amount;
  const availabilityMedianType = availability?.median?.type;

  const isMarketMedian = availabilityMedianType === 'market';
  const isUserMedian = availabilityMedianType === 'user';

  const isSupportedMedianType = isMarketMedian || isUserMedian;

  if (!availabilityMedianPrice || !isSupportedMedianType) {
    return null;
  }

  const marketCurrency = marketInformation?.currency;

  const Icon = isUserMedian ? User : MerchantBadge;

  return (
    <div className={styles.medianPriceWidget}>
      <div className={styles.medianPrice}>
        <div className={styles.icon} aria-label={isUserMedian ? 'User Icon' : 'Merchant Icon'}>
          {<SsrSvg component={<Icon />} />}
        </div>
        <span className={styles.amount}>
          {priceToString({
            amount: availabilityMedianPrice,
            currency: marketCurrency,
          })}
        </span>
      </div>
      {isUserMedian ? (
        t(TRANSLATIONS.averageOfAllUsersReportedPrices)
      ) : (
        <BuyingOptions vintage={vintage} ppcContext />
      )}
    </div>
  );
};

createMedianPriceWidget.propTypes = {
  vintage: vintage.isRequired,
  availability: PropTypes.object,
  marketInformation: PropTypes.object,
};

export const getExplorerUrlParams = (averageRating, vintage, currency) => {
  const baseParams = {
    wineStyleIds: [vintage.wine?.style?.id],
    priceRangeMin: 1,
    priceRangeMax: 'max',
    currencyCode: currency?.code,
  };

  const explorerParams = averageRating
    ? { minRating: averageRating, orderBy: ORDER_BY_PRICE, order: ORDER_ASC }
    : { orderBy: ORDER_BY_RATINGS_COUNT, order: ORDER_DESC, minRating: 1 };

  return { ...baseParams, ...explorerParams };
};

const createBetterExplorerValueWidget = (vintage, betterValueExplorerVintage = null, t) => {
  if (!betterValueExplorerVintage) {
    return null;
  }

  let averageRating = vintage.has_valid_ratings ? vintage.statistics.ratings_average : null;

  if (!averageRating) {
    averageRating = vintage.wine?.has_valid_ratings
      ? vintage.wine.statistics.ratings_average
      : null;
  }

  const currency = betterValueExplorerVintage.price?.currency;
  const wineStyleName = vintage.wine?.style?.name;
  const formattedAmount = priceToString({
    amount: betterValueExplorerVintage.price?.amount,
    currency,
  });

  const url = explorerUrl(
    // @ts-ignore
    getExplorerUrlParams(averageRating, vintage, currency)
  );

  return (
    <div className={styles.betterValueWidget}>
      <p className={styles.betterValueSentence}>
        {averageRating !== null
          ? t(TRANSLATIONS.betterValue, {
              better_value: <strong key="betterValueTag">{t(TRANSLATIONS.betterValueTag)}</strong>,
              ratings_average: averageRating,
              wine_style: wineStyleName,
              amount: formattedAmount,
            })
          : t(TRANSLATIONS.goodToKnow, {
              good_to_know: <strong key="goodToKnowTag">{t(TRANSLATIONS.goodToKnowTag)}</strong>,
              wine_style: wineStyleName,
              amount: formattedAmount,
            })}
      </p>
      <Link href={url} className={styles.exploreVintages}>
        {t(TRANSLATIONS.exploreSimilarWinesVariantD, {
          wine_style: wineStyleName,
        })}
      </Link>
    </div>
  );
};

const PurchaseAvailabilityPPC = ({
  availability,
  vintage,
  marketInformation,
  betterValueExplorerVintage,
}) => {
  const { t } = useI18N();
  const medianPriceWidget = createMedianPriceWidget({
    t,
    vintage,
    availability,
    marketInformation,
  });

  const betterValueWidget = createBetterExplorerValueWidget(vintage, betterValueExplorerVintage, t);

  if (!medianPriceWidget && !betterValueWidget) {
    return null;
  }

  return (
    <div className={styles.ppcAvailabilityContent}>
      {medianPriceWidget}
      {medianPriceWidget && betterValueWidget && <div className={styles.separator} />}
      {betterValueWidget}
    </div>
  );
};

PurchaseAvailabilityPPC.propTypes = {
  availability: PropTypes.object,
  vintage: PropTypes.object,
  marketInformation: PropTypes.object,
  betterValueExplorerVintage: PropTypes.object,
  wineStyleMedianExperimentVariant: PropTypes.object,
};

export default PurchaseAvailabilityPPC;
